import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Chip,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Tooltip,
  Fade,
  InputAdornment,
  Divider,
  Badge,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GavelIcon from '@mui/icons-material/Gavel';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import BalanceIcon from '@mui/icons-material/Balance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useAuth } from '../../contexts/AuthContext';
import {
  buscarProcessos,
  buscarProcessoPorCNJ,
  buscarMultiplosTribunais,
  validarCNJ,
  formatarCNJ,
  tribunais,
  DataJudSearchParams,
  DataJudResult,
  DataJudResponse,
} from '../../services/datajudService';
import { useNavigate as useNav } from 'react-router-dom';
import api from '../../services/api';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ProcessedResult {
  id: string;
  numeroProcesso: string;
  classeProcessual: string;
  assunto: string;
  assuntos: string[];
  orgaoJulgador: string;
  juiz: string;
  dataAjuizamento: string;
  dataUltimaAtualizacao: string;
  tribunal: string;
  sigla: string;
  grau: string;
  nivelSigilo: string;
  movimentos: any[];
  partes: any[];
  valorCausa: number | null;
  bookmarked: boolean;
  monitored: boolean;
  raw: DataJudResult;
}

interface SearchFilters {
  query: string;
  tribunal: string;
  grau: string;
  dataInicial: string;
  dataFinal: string;
  multiplosTribunais: boolean;
  tribunaisSelecionados: string[];
}

// Prazos CPC por tipo de movimento (em dias)
const PRAZOS_CPC: Record<string, { dias: number; descricao: string }> = {
  'citação': { dias: 15, descricao: 'Contestação (art. 335 CPC)' },
  'intimação': { dias: 15, descricao: 'Manifestação (art. 231 CPC)' },
  'sentença': { dias: 15, descricao: 'Apelação (art. 1.003 CPC)' },
  'acórdão': { dias: 15, descricao: 'Embargos/Recurso (art. 1.003 CPC)' },
  'despacho': { dias: 5, descricao: 'Resposta ao despacho (art. 218 CPC)' },
  'decisão': { dias: 15, descricao: 'Agravo/Recurso (art. 1.003 CPC)' },
  'embargos': { dias: 5, descricao: 'Resposta aos embargos (art. 1.023 CPC)' },
  'penhora': { dias: 15, descricao: 'Impugnação (art. 525 CPC)' },
  'laudo': { dias: 15, descricao: 'Manifestação sobre laudo (art. 477 CPC)' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const extractName = (field: any): string => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object' && field.nome) return field.nome;
  if (Array.isArray(field) && field.length > 0)
    return field.map((i) => extractName(i)).join(', ');
  return '';
};

// Aceita 'YYYY-MM-DD', ISO completo e o formato cru do DataJud ('20260721000000').
const formatDate = (d: string) => {
  const m = /^(\d{4})-?(\d{2})-?(\d{2})/.exec(d || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

const daysSince = (d: string): number => {
  if (!d) return 0;
  try {
    return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  } catch { return 0; }
};

const addBusinessDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return result;
};

const detectGrauFromCNJ = (cnj: string): string => {
  if (!cnj || cnj.length < 14) return '';
  const segment = cnj.replace(/\D/g, '').substring(13, 14);
  const map: Record<string, string> = { '8': 'Estadual', '5': 'Federal', '4': 'Trabalho', '7': 'Eleitoral', '9': 'Militar' };
  return map[segment] || '';
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ movimentos: any[]; dataUltimaAtualizacao: string }> = ({
  movimentos,
  dataUltimaAtualizacao,
}) => {
  const days = daysSince(dataUltimaAtualizacao);
  const lastMov = movimentos[movimentos.length - 1];
  const lastName = lastMov?.nome || '';

  const isArchived = ['arquivado', 'extinto', 'baixa', 'encerrado'].some((k) =>
    lastName.toLowerCase().includes(k)
  );
  const isUrgent = ['citação', 'intimação', 'penhora', 'bloqueio'].some((k) =>
    lastName.toLowerCase().includes(k)
  );

  if (isArchived)
    return <Chip size="small" label="Arquivado" color="default" icon={<CheckCircleOutlineIcon />} />;
  if (isUrgent)
    return <Chip size="small" label="Atenção" color="error" icon={<WarningAmberIcon />} />;
  if (days > 60)
    return <Chip size="small" label={`${days}d sem mov.`} color="warning" icon={<AccessTimeIcon />} />;
  return <Chip size="small" label="Ativo" color="success" icon={<CheckCircleOutlineIcon />} />;
};

const MovimentoIcon: React.FC<{ nome: string }> = ({ nome }) => {
  const n = nome.toLowerCase();
  if (n.includes('sentença') || n.includes('acórdão') || n.includes('decisão'))
    return <GavelIcon fontSize="small" color="primary" />;
  if (n.includes('citação') || n.includes('intimação'))
    return <WarningAmberIcon fontSize="small" color="error" />;
  if (n.includes('penhora') || n.includes('bloqueio'))
    return <WarningAmberIcon fontSize="small" color="warning" />;
  if (n.includes('arquivado') || n.includes('extinto'))
    return <CheckCircleOutlineIcon fontSize="small" color="disabled" />;
  return <InfoOutlinedIcon fontSize="small" color="action" />;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ProcessedResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedTab, setExpandedTab] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [monitored, setMonitored] = useState<string[]>([]);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);

  // Deadline dialog
  const [deadlineProcess, setDeadlineProcess] = useState<ProcessedResult | null>(null);
  const [showDeadlineDialog, setShowDeadlineDialog] = useState(false);

  // Monitor dialog
  const [monitorTarget, setMonitorTarget] = useState<ProcessedResult | null>(null);
  const [monitorLabel, setMonitorLabel] = useState('');
  const [monitorEmail, setMonitorEmail] = useState(true);
  const [showMonitorDialog, setShowMonitorDialog] = useState(false);
  const [savingMonitor, setSavingMonitor] = useState(false);

  const [filters, setFilters] = useState<SearchFilters>({
    query: searchParams.get('q') || searchParams.get('cnj') || '',
    tribunal: searchParams.get('tribunal') || 'TJSP',
    grau: '',
    dataInicial: '',
    dataFinal: '',
    multiplosTribunais: false,
    tribunaisSelecionados: ['TJSP'],
  });

  const resultsPerPage = 10;

  useEffect(() => {
    const saved = localStorage.getItem('search_favorites');
    if (saved) setFavorites(JSON.parse(saved));
    const savedMon = localStorage.getItem('monitored_processes');
    if (savedMon) setMonitored(JSON.parse(savedMon));
    const savedHist = localStorage.getItem('search_history_queries');
    if (savedHist) setSearchHistory(JSON.parse(savedHist));
  }, []);

  const saveHistory = (q: string) => {
    if (!q.trim()) return;
    const next = [q, ...searchHistory.filter((h) => h !== q)].slice(0, 8);
    setSearchHistory(next);
    localStorage.setItem('search_history_queries', JSON.stringify(next));
  };

  // ── Process results ────────────────────────────────────────────────────────

  const processRaw = (raw: DataJudResult, tribunalSigla: string): ProcessedResult => {
    const classe = extractName(raw.classe ?? (raw as any).classeProcessual);
    const assuntosArr: any[] = Array.isArray((raw as any).assuntos)
      ? (raw as any).assuntos
      : raw.assunto
        ? [raw.assunto]
        : [];
    const assuntoNomes = assuntosArr.map((a: any) => extractName(a)).filter(Boolean);
    const orgao = extractName(raw.orgaoJulgador);
    const movs: any[] = Array.isArray(raw.movimentos) ? raw.movimentos : [];
    const partes: any[] = Array.isArray(raw.partes) ? raw.partes : [];

    const dataAtu =
      (raw as any).dataHoraUltimaAtualizacao ||
      (raw as any)['@timestamp'] ||
      (raw as any).dataAtualizacao ||
      '';

    const juiz = (() => {
      for (const m of movs) {
        const c = m.complemento || '';
        const match = c.match(/(?:relator|rel\.|juiz|juíza|desembargador|des\.|ministro|min\.)\s*:?\s*([^,\n.;]+)/i);
        if (match) return match[1].trim();
      }
      return '';
    })();

    return {
      id: raw.id || `${raw.numeroProcesso}-${Date.now()}`,
      numeroProcesso: raw.numeroProcesso || '',
      classeProcessual: classe || 'Não informado',
      assunto: assuntoNomes[0] || 'Não informado',
      assuntos: assuntoNomes,
      orgaoJulgador: orgao || 'Não informado',
      juiz,
      dataAjuizamento: raw.dataAjuizamento || '',
      dataUltimaAtualizacao: dataAtu,
      tribunal: (raw as any).tribunal || tribunais[tribunalSigla as keyof typeof tribunais]?.nome || tribunalSigla,
      sigla: tribunalSigla,
      grau: raw.grau || '',
      nivelSigilo: String((raw as any).nivelSigilo ?? 'Público'),
      movimentos: movs,
      partes,
      valorCausa: (raw as any).valorCausa || null,
      bookmarked: favorites.includes(raw.id || raw.numeroProcesso),
      monitored: monitored.includes(raw.numeroProcesso || ''),
      raw,
    };
  };

  // ── Search ────────────────────────────────────────────────────────────────

  const isCNJ = (v: string) => v.replace(/\D/g, '').length === 20;

  const performSearch = useCallback(async () => {
    const q = filters.query.trim();
    if (!q) { setError('Informe um número de processo, assunto ou classe processual.'); return; }

    setLoading(true);
    setError(null);
    setResults([]);
    setTotalResults(0);
    saveHistory(q);

    try {
      if (isCNJ(q)) {
        const raw = await buscarProcessoPorCNJ(q.replace(/\D/g, ''), filters.tribunal);
        if (raw) {
          setResults([processRaw(raw, filters.tribunal)]);
          setTotalResults(1);
        } else {
          setError('Processo não encontrado no tribunal selecionado. Tente outro tribunal.');
        }
      } else if (filters.multiplosTribunais && filters.tribunaisSelecionados.length > 0) {
        const res = await buscarMultiplosTribunais({
          assunto: q,
          tribunal: filters.tribunaisSelecionados[0],
          tribunais: filters.tribunaisSelecionados,
          dataInicial: filters.dataInicial || undefined,
          dataFinal: filters.dataFinal || undefined,
          page,
          size: resultsPerPage,
        });
        const processed = (res.data as DataJudResult[]).map((r) =>
          processRaw(r, (r as any).tribunal || filters.tribunaisSelecionados[0])
        );
        setResults(processed);
        setTotalResults(res.total_results || processed.length);
      } else {
        const sp: DataJudSearchParams = {
          assunto: q,
          tribunal: filters.tribunal,
          dataInicial: filters.dataInicial || undefined,
          dataFinal: filters.dataFinal || undefined,
          page,
          size: resultsPerPage,
        };
        const res: DataJudResponse = await buscarProcessos(sp);
        const hits = res.hits?.hits ?? [];
        const processed = hits.map((h) => processRaw(h._source, filters.tribunal));
        setResults(processed);
        setTotalResults(
          typeof res.hits?.total === 'object'
            ? res.hits.total.value
            : (res.hits?.total as any) ?? processed.length
        );
      }

      const p = new URLSearchParams();
      p.set(isCNJ(q) ? 'cnj' : 'q', q);
      if (filters.tribunal) p.set('tribunal', filters.tribunal);
      setSearchParams(p);
    } catch (e: any) {
      console.error(e);
      // mensagem do servidor (ex.: tribunal que não respondeu no DataJud), não o texto técnico do axios
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail
        : e?.response ? 'Erro ao consultar o DataJud. Tente novamente em alguns minutos.'
        : e?.message || 'Erro ao consultar o DataJud. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, favorites, monitored]);

  useEffect(() => {
    const q = searchParams.get('q') || searchParams.get('cnj');
    if (q) {
      setFilters((f) => ({ ...f, query: q }));
      setTimeout(() => performSearch(), 150);
    }
  }, []); // eslint-disable-line

  // ── Bookmark & Monitor ────────────────────────────────────────────────────

  const toggleBookmark = (r: ProcessedResult) => {
    const next = favorites.includes(r.id)
      ? favorites.filter((f) => f !== r.id)
      : [...favorites, r.id];
    setFavorites(next);
    localStorage.setItem('search_favorites', JSON.stringify(next));
    setResults((prev) =>
      prev.map((p) => (p.id === r.id ? { ...p, bookmarked: !p.bookmarked } : p))
    );
  };

  const openMonitorDialog = (r: ProcessedResult) => {
    setMonitorTarget(r);
    setMonitorLabel(r.classeProcessual !== 'Não informado' ? `${r.classeProcessual} - ${r.tribunal}` : r.numeroProcesso);
    setMonitorEmail(true);
    setShowMonitorDialog(true);
  };

  const saveMonitor = async () => {
    if (!monitorTarget) return;
    setSavingMonitor(true);
    try {
      await api.post('/process-alerts', {
        cnj_number: monitorTarget.numeroProcesso,
        tribunal: monitorTarget.sigla,
        label: monitorLabel,
        email_enabled: monitorEmail,
      });
      const next = [...monitored, monitorTarget.numeroProcesso];
      setMonitored(next);
      localStorage.setItem('monitored_processes', JSON.stringify(next));
      setResults((prev) =>
        prev.map((p) => (p.id === monitorTarget.id ? { ...p, monitored: true } : p))
      );
      setShowMonitorDialog(false);
    } catch {
      // fallback: save locally
      const next = [...monitored, monitorTarget.numeroProcesso];
      setMonitored(next);
      localStorage.setItem('monitored_processes', JSON.stringify(next));
      setResults((prev) =>
        prev.map((p) => (p.id === monitorTarget.id ? { ...p, monitored: true } : p))
      );
      setShowMonitorDialog(false);
    } finally {
      setSavingMonitor(false);
    }
  };

  // ── AI Summary ────────────────────────────────────────────────────────────

  const sendToAI = (r: ProcessedResult) => {
    const lastMov = r.movimentos[r.movimentos.length - 1];
    const message = (
      `Analise este processo judicial e resuma os pontos principais:\n\n` +
      `Processo: ${formatarCNJ(r.numeroProcesso)}\n` +
      `Tribunal: ${r.tribunal}\n` +
      `Classe: ${r.classeProcessual}\n` +
      `Assunto: ${r.assunto}\n` +
      `Órgão: ${r.orgaoJulgador}\n` +
      `Ajuizamento: ${formatDate(r.dataAjuizamento)}\n` +
      `Última movimentação: ${lastMov?.nome || '—'} em ${formatDate(lastMov?.dataHora || '')}\n` +
      `Total de movimentações: ${r.movimentos.length}`
    );
    // O chat recebe a mensagem inicial por state (como no painel); ?message= não é lido
    navigate('/chat', { state: { initialMessage: message } });
  };

  // ── Deadline dialog ───────────────────────────────────────────────────────

  const openDeadline = (r: ProcessedResult) => {
    setDeadlineProcess(r);
    setShowDeadlineDialog(true);
  };

  const getDeadlines = (r: ProcessedResult) => {
    const lastMov = r.movimentos[r.movimentos.length - 1];
    if (!lastMov) return [];
    const nome = (lastMov.nome || '').toLowerCase();
    const dataHora = lastMov.dataHora || r.dataUltimaAtualizacao;
    const baseDate = dataHora ? new Date(dataHora) : new Date();

    return Object.entries(PRAZOS_CPC)
      .filter(([key]) => nome.includes(key))
      .map(([, val]) => ({
        ...val,
        deadline: addBusinessDays(baseDate, val.dias),
        baseDate,
      }));
  };

  // ── Document integration ──────────────────────────────────────────────────

  const useInDocument = (r: ProcessedResult) => {
    const lastMov = r.movimentos[r.movimentos.length - 1];
    // Dados do processo seguem como contexto para o Documento Livre (o wizard antigo foi desativado)
    const context = [
      `Processo nº ${formatarCNJ(r.numeroProcesso)} (${r.tribunal})`,
      r.classeProcessual && `Classe: ${r.classeProcessual}`,
      r.assunto && `Assunto: ${r.assunto}`,
      r.orgaoJulgador && `Órgão julgador: ${r.orgaoJulgador}`,
      r.dataAjuizamento && `Ajuizamento: ${formatDate(r.dataAjuizamento)}`,
      lastMov?.nome && `Último andamento: ${lastMov.nome}`,
    ].filter(Boolean).join('\n');
    navigate('/documents/flow/documento-livre', { state: { fromChat: true, context } });
  };

  const tribunaisPorCategoria: Record<string, string[]> = {
    'Superiores': Object.keys(tribunais).filter((t) =>
      ['STJ', 'STM', 'TST', 'TSE'].includes(t)
    ),
    'Justiça Federal': Object.keys(tribunais).filter((t) => t.startsWith('TRF')),
    'Justiça do Trabalho': Object.keys(tribunais).filter((t) => t.startsWith('TRT')),
    'Justiça Estadual': Object.keys(tribunais).filter((t) => t.startsWith('TJ')),
  };

  const totalPages = Math.ceil(totalResults / resultsPerPage);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="xl" sx={{ pt: 2, pb: 6 }}>
      {/* ── Header ── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <BalanceIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Consulta Processual
          </Typography>
          <Chip label="DataJud / CNJ" size="small" variant="outlined" color="primary" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Consulte processos de todos os tribunais brasileiros. Digite o número CNJ ou um assunto/classe processual.
        </Typography>
      </Box>

      {/* ── Smart Search Bar ── */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            placeholder="Nº CNJ (00000000000000000000), assunto ou classe processual..."
            value={filters.query}
            onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && performSearch()}
            sx={{ flex: 1, minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: filters.query && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilters((f) => ({ ...f, query: '' }))}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {!filters.multiplosTribunais && (
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Tribunal</InputLabel>
              <Select
                value={filters.tribunal}
                label="Tribunal"
                onChange={(e) => setFilters((f) => ({ ...f, tribunal: e.target.value }))}
              >
                {Object.entries(tribunais).map(([cod, info]) => (
                  <MenuItem key={cod} value={cod}>
                    {cod} — {info.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            onClick={performSearch}
            disabled={loading}
            sx={{ minWidth: 110, borderRadius: 2 }}
          >
            Buscar
          </Button>

          <Tooltip title="Filtros avançados">
            <IconButton
              onClick={() => setShowFilters((v) => !v)}
              color={showFilters ? 'primary' : 'default'}
              sx={{
                border: `1px solid ${showFilters ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Smart hints */}
        {filters.query && isCNJ(filters.query) && (
          <Alert severity="info" sx={{ mt: 1 }} icon={<InfoOutlinedIcon />}>
            Número CNJ detectado — buscando processo específico em{' '}
            <strong>{filters.multiplosTribunais ? 'múltiplos tribunais' : filters.tribunal}</strong>
          </Alert>
        )}

        {/* History chips */}
        {!filters.query && searchHistory.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Buscas recentes:
            </Typography>
            {searchHistory.map((h, i) => (
              <Chip
                key={i}
                label={h}
                size="small"
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }}
                onClick={() => {
                  setFilters((f) => ({ ...f, query: h }));
                  setTimeout(performSearch, 100);
                }}
              />
            ))}
          </Box>
        )}

        {/* Advanced filters collapse */}
        <Collapse in={showFilters}>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grau</InputLabel>
                  <Select
                    value={filters.grau}
                    label="Grau"
                    onChange={(e) => setFilters((f) => ({ ...f, grau: e.target.value }))}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="G1">1º Grau (G1)</MenuItem>
                    <MenuItem value="G2">2º Grau (G2)</MenuItem>
                    <MenuItem value="JE">Juizados Especiais (JE)</MenuItem>
                    <MenuItem value="SUP">Superior</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data inicial"
                  type="date"
                  value={filters.dataInicial}
                  onChange={(e) => setFilters((f) => ({ ...f, dataInicial: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data final"
                  type="date"
                  value={filters.dataFinal}
                  onChange={(e) => setFilters((f) => ({ ...f, dataFinal: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.multiplosTribunais}
                      onChange={(e) => setFilters((f) => ({ ...f, multiplosTribunais: e.target.checked }))}
                    />
                  }
                  label="Múltiplos tribunais"
                />
              </Grid>
            </Grid>

            {filters.multiplosTribunais && (
              <Box sx={{ mt: 2 }}>
                {Object.entries(tribunaisPorCategoria).map(([cat, list]) => (
                  <Box key={cat} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {cat}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {list.map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          variant={filters.tribunaisSelecionados.includes(t) ? 'filled' : 'outlined'}
                          color={filters.tribunaisSelecionados.includes(t) ? 'primary' : 'default'}
                          onClick={() =>
                            setFilters((f) => ({
                              ...f,
                              tribunaisSelecionados: f.tribunaisSelecionados.includes(t)
                                ? f.tribunaisSelecionados.filter((x) => x !== t)
                                : [...f.tribunaisSelecionados, t],
                            }))
                          }
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* ── Error ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Loading ── */}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Results header ── */}
      {results.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            <strong>{totalResults.toLocaleString('pt-BR')}</strong>{' '}
            {totalResults === 1 ? 'processo encontrado' : 'processos encontrados'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => {
              const header = 'Processo,Tribunal,Classe,Assunto,Órgão,Ajuizamento,Última atualização\n';
              const rows = results
                .map(
                  (r) =>
                    `"${formatarCNJ(r.numeroProcesso)}","${r.tribunal}","${r.classeProcessual}","${r.assunto}","${r.orgaoJulgador}","${formatDate(r.dataAjuizamento)}","${formatDate(r.dataUltimaAtualizacao)}"`
                )
                .join('\n');
              const blob = new Blob(['\uFEFF' + header + rows], { type: 'text/csv;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `processos_${new Date().toISOString().slice(0, 10)}.csv`;
              a.click();
            }}
          >
            Exportar CSV
          </Button>
        </Box>
      )}

      {/* ── Result Cards ── */}
      <Stack spacing={2}>
        {results.map((r) => {
          const days = daysSince(r.dataUltimaAtualizacao);
          const lastMov = r.movimentos[r.movimentos.length - 1];
          const isExpanded = expandedId === r.id;

          return (
            <Card
              key={r.id}
              elevation={isExpanded ? 3 : 1}
              sx={{
                borderRadius: 3,
                border: `1px solid ${isExpanded ? theme.palette.primary.main : theme.palette.divider}`,
                transition: 'all 0.2s',
                '&:hover': { boxShadow: theme.shadows[3] },
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                {/* ─ Card Header ─ */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Left: Process number + status */}
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography
                        variant="h6"
                        component="span"
                        fontWeight={700}
                        fontFamily="monospace"
                        sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' } }}
                      >
                        {formatarCNJ(r.numeroProcesso) || 'Processo sem número'}
                      </Typography>
                      <Tooltip title="Copiar número">
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard.writeText(formatarCNJ(r.numeroProcesso))}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <StatusBadge movimentos={r.movimentos} dataUltimaAtualizacao={r.dataUltimaAtualizacao} />
                      {r.nivelSigilo !== '0' && r.nivelSigilo !== 'Público' && (
                        <Chip label={`Sigilo ${r.nivelSigilo}`} size="small" color="warning" />
                      )}
                    </Box>

                    {/* Tags line */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                      <Chip label={r.sigla || r.tribunal} size="small" color="primary" />
                      {r.grau && (
                        <Chip label={r.grau} size="small" variant="outlined" />
                      )}
                      {r.classeProcessual !== 'Não informado' && (
                        <Chip label={r.classeProcessual} size="small" variant="outlined" />
                      )}
                      {detectGrauFromCNJ(r.numeroProcesso) && (
                        <Chip
                          label={detectGrauFromCNJ(r.numeroProcesso)}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                    </Box>

                    {/* Key info row */}
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      {r.assunto !== 'Não informado' && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Assunto:</strong> {r.assunto}
                          </Typography>
                        </Grid>
                      )}
                      {r.orgaoJulgador !== 'Não informado' && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Órgão:</strong> {r.orgaoJulgador}
                          </Typography>
                        </Grid>
                      )}
                      {r.juiz && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            <PersonIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.3 }} />
                            <strong>Magistrado:</strong> {r.juiz}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <CalendarMonthIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.3 }} />
                          <strong>Ajuizamento:</strong> {formatDate(r.dataAjuizamento)}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Last movement highlight */}
                    {lastMov && (
                      <Paper
                        variant="outlined"
                        sx={{
                          px: 2,
                          py: 1,
                          bgcolor: days > 60
                            ? alpha(theme.palette.warning.main, 0.06)
                            : alpha(theme.palette.success.main, 0.04),
                          borderColor: days > 60
                            ? theme.palette.warning.light
                            : theme.palette.success.light,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <MovimentoIcon nome={lastMov.nome || ''} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            Última movimentação
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {lastMov.nome} —{' '}
                            {formatDate(lastMov.dataHora || r.dataUltimaAtualizacao)}
                            {days > 0 && (
                              <Typography
                                component="span"
                                variant="caption"
                                color={days > 60 ? 'warning.main' : 'text.secondary'}
                                sx={{ ml: 1 }}
                              >
                                ({days}d atrás)
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                        {days > 30 && (
                          <Chip
                            label={`${days}d sem mov.`}
                            size="small"
                            color={days > 60 ? 'warning' : 'default'}
                            icon={<AccessTimeIcon />}
                          />
                        )}
                      </Paper>
                    )}
                  </Box>

                  {/* Right: action buttons */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 40 }}>
                    <Tooltip title={r.monitored ? 'Monitorado' : 'Monitorar processo'}>
                      <IconButton
                        size="small"
                        color={r.monitored ? 'primary' : 'default'}
                        onClick={() => r.monitored ? navigate('/alertas') : openMonitorDialog(r)}
                      >
                        {r.monitored ? (
                          <Badge color="primary" variant="dot">
                            <NotificationsActiveIcon />
                          </Badge>
                        ) : (
                          <NotificationsNoneIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={r.bookmarked ? 'Remover favorito' : 'Favoritar'}>
                      <IconButton
                        size="small"
                        color={r.bookmarked ? 'warning' : 'default'}
                        onClick={() => toggleBookmark(r)}
                      >
                        {r.bookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Abrir no CNJ">
                      <IconButton
                        size="small"
                        component="a"
                        href={`https://www.cnj.jus.br/processo/${r.numeroProcesso}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* ─ Quick action toolbar ─ */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SmartToyIcon />}
                    onClick={() => sendToAI(r)}
                  >
                    Resumir com IA
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CalendarMonthIcon />}
                    onClick={() => openDeadline(r)}
                    disabled={r.movimentos.length === 0}
                  >
                    Calcular Prazo
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DescriptionIcon />}
                    onClick={() => useInDocument(r)}
                  >
                    Usar em Petição
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<ExpandMoreIcon
                      sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                    />}
                    onClick={() => {
                      setExpandedId(isExpanded ? null : r.id);
                      setExpandedTab(0);
                    }}
                  >
                    {isExpanded ? 'Menos detalhes' : `Ver detalhes (${r.movimentos.length} mov.)`}
                  </Button>
                </Box>

                {/* ─ Expanded detail ─ */}
                <Collapse in={isExpanded}>
                  <Box sx={{ mt: 2 }}>
                    <Tabs
                      value={expandedTab}
                      onChange={(_, v) => setExpandedTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      <Tab label="Informações" />
                      <Tab label={`Movimentações (${r.movimentos.length})`} />
                      {r.partes.length > 0 && <Tab label={`Partes (${r.partes.length})`} />}
                      {r.valorCausa && <Tab label="Valores" />}
                    </Tabs>
                    <Divider />

                    {/* Tab 0: Info */}
                    {expandedTab === 0 && (
                      <Fade in timeout={250}>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                              <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                                Dados Processuais
                              </Typography>
                              {[
                                ['Número', formatarCNJ(r.numeroProcesso)],
                                ['Classe', r.classeProcessual],
                                ['Assunto principal', r.assunto],
                                ['Outros assuntos', r.assuntos.slice(1).join(', ') || '—'],
                                ['Grau', r.grau || '—'],
                                ['Sigilo', r.nivelSigilo],
                              ].map(([label, val]) => (
                                <Box key={label} sx={{ mb: 0.5 }}>
                                  <Typography variant="body2">
                                    <strong>{label}:</strong>{' '}
                                    <span style={{ fontFamily: label === 'Número' ? 'monospace' : undefined }}>
                                      {val}
                                    </span>
                                  </Typography>
                                </Box>
                              ))}
                            </Paper>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                              <Typography variant="subtitle2" color="secondary" fontWeight={700} gutterBottom>
                                Órgão & Datas
                              </Typography>
                              {[
                                ['Tribunal', r.tribunal],
                                ['Órgão julgador', r.orgaoJulgador],
                                ['Magistrado', r.juiz || '—'],
                                ['Ajuizamento', formatDate(r.dataAjuizamento)],
                                ['Última atualização', formatDate(r.dataUltimaAtualizacao)],
                              ].map(([label, val]) => (
                                <Box key={label} sx={{ mb: 0.5 }}>
                                  <Typography variant="body2">
                                    <strong>{label}:</strong> {val}
                                  </Typography>
                                </Box>
                              ))}
                            </Paper>
                          </Grid>
                        </Grid>
                      </Fade>
                    )}

                    {/* Tab 1: Movements timeline */}
                    {expandedTab === 1 && (
                      <Fade in timeout={250}>
                        <Box sx={{ mt: 2, maxHeight: 400, overflow: 'auto', pr: 1 }}>
                          {r.movimentos.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                              Nenhuma movimentação disponível.
                            </Typography>
                          ) : (
                            [...r.movimentos].reverse().map((m, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  mb: 1.5,
                                  position: 'relative',
                                  '&:not(:last-child)::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 11,
                                    top: 28,
                                    bottom: -6,
                                    width: 2,
                                    bgcolor: 'divider',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: idx === 0 ? 'primary.main' : 'grey.200',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mt: 0.3,
                                  }}
                                >
                                  <MovimentoIcon nome={m.nome || ''} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                    <Typography variant="body2" fontWeight={idx === 0 ? 700 : 400}>
                                      {m.nome || 'Movimentação'}
                                      {idx === 0 && (
                                        <Chip label="ÚLTIMO" size="small" color="primary" sx={{ ml: 1, height: 18 }} />
                                      )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatDate(m.dataHora)}
                                    </Typography>
                                  </Box>
                                  {m.complemento && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                                      {String(m.complemento).slice(0, 200)}
                                      {String(m.complemento).length > 200 ? '...' : ''}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            ))
                          )}
                        </Box>
                      </Fade>
                    )}

                    {/* Tab 2: Partes */}
                    {expandedTab === 2 && r.partes.length > 0 && (
                      <Fade in timeout={250}>
                        <Grid container spacing={1.5} sx={{ mt: 1 }}>
                          {r.partes.map((parte: any, i: number) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {extractName(parte) || 'Parte não identificada'}
                                </Typography>
                                {parte.polo && (
                                  <Chip
                                    label={parte.polo}
                                    size="small"
                                    color={parte.polo.toLowerCase().includes('at') ? 'success' : 'info'}
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Fade>
                    )}

                    {/* Tab 3: Values */}
                    {expandedTab === 3 && r.valorCausa && (
                      <Fade in timeout={250}>
                        <Box sx={{ mt: 2 }}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'inline-block' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Valor da Causa
                            </Typography>
                            <Typography variant="h5" color="success.main" fontWeight={700}>
                              {r.valorCausa.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })}
                            </Typography>
                          </Paper>
                        </Box>
                      </Fade>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => {
              setPage(v);
              setTimeout(() => performSearch(), 100);
            }}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* ── Empty state ── */}
      {!loading && results.length === 0 && !error && (
        <Paper
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 3,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <BalanceIcon sx={{ fontSize: 56, color: 'grey.300', mb: 1.5 }} />
          <Typography variant="h6" gutterBottom color="text.secondary">
            Consulte processos do DataJud / CNJ
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Pesquise por número CNJ, assunto ou classe processual em qualquer tribunal brasileiro.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
            {['dano moral', 'execução fiscal', 'mandado de segurança', 'rescisão contratual'].map(
              (ex) => (
                <Chip
                  key={ex}
                  label={ex}
                  variant="outlined"
                  sx={{ cursor: 'pointer', mb: 1 }}
                  onClick={() => {
                    setFilters((f) => ({ ...f, query: ex }));
                    setTimeout(() => performSearch(), 100);
                  }}
                />
              )
            )}
          </Stack>
        </Paper>
      )}

      {/* ── Deadline Dialog ── */}
      <Dialog
        open={showDeadlineDialog}
        onClose={() => setShowDeadlineDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <CalendarMonthIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Calculadora de Prazos
        </DialogTitle>
        <DialogContent>
          {deadlineProcess && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Processo:{' '}
                <strong>{formatarCNJ(deadlineProcess.numeroProcesso)}</strong>
              </Typography>
              {(() => {
                const lastMov = deadlineProcess.movimentos[deadlineProcess.movimentos.length - 1];
                const deadlines = getDeadlines(deadlineProcess);
                return (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>Última movimentação:</strong> {lastMov?.nome || '—'} em{' '}
                      {formatDate(lastMov?.dataHora || '')}
                    </Alert>
                    {deadlines.length === 0 ? (
                      <Typography color="text.secondary">
                        Nenhum prazo mapeado para "{lastMov?.nome}". Consulte o CPC ou seu advogado.
                      </Typography>
                    ) : (
                      deadlines.map((d, i) => (
                        <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {d.descricao}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Prazo: <strong>{d.dias} dias úteis</strong>
                          </Typography>
                          <Typography variant="h6" color="primary" sx={{ mt: 0.5 }}>
                            Vencimento: {d.deadline.toLocaleDateString('pt-BR')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Calculado a partir de {d.baseDate.toLocaleDateString('pt-BR')}
                          </Typography>
                        </Paper>
                      ))
                    )}
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Cálculo estimado. Sempre confirme com o sistema do tribunal e legislação aplicável.
                    </Alert>
                  </>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeadlineDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* ── Monitor Dialog ── */}
      <Dialog
        open={showMonitorDialog}
        onClose={() => setShowMonitorDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <NotificationsNoneIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Monitorar Processo
        </DialogTitle>
        <DialogContent>
          {monitorTarget && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Processo: <strong>{formatarCNJ(monitorTarget.numeroProcesso)}</strong>
              </Typography>
              <TextField
                fullWidth
                label="Rótulo (opcional)"
                placeholder="Ex: Caso Silva vs. Banco"
                value={monitorLabel}
                onChange={(e) => setMonitorLabel(e.target.value)}
                sx={{ my: 1.5 }}
                size="small"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={monitorEmail}
                    onChange={(e) => setMonitorEmail(e.target.checked)}
                  />
                }
                label="Receber alertas por e-mail"
              />
              <Alert severity="info" sx={{ mt: 1.5 }} icon={<NotificationsNoneIcon />}>
                Você será notificado quando houver novas movimentações neste processo.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMonitorDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={saveMonitor}
            disabled={savingMonitor}
            startIcon={savingMonitor ? <CircularProgress size={16} /> : <NotificationsNoneIcon />}
          >
            Monitorar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SearchPage;
